import { useEffect, useRef, forwardRef, MutableRefObject, Dispatch, SetStateAction, useLayoutEffect } from 'react';
import eyeImg from '@/assets/eyeBall.png';
import { isMostlyGreen } from './utils';
import EagleImg from '@/assets/EagleHead.png';

type Props = {
  setEyeRdy: Dispatch<SetStateAction<boolean>>;
  expanded: boolean;
};

const AnimatedEagleEye = forwardRef<HTMLDivElement, Props>(({ setEyeRdy, expanded }, ref) => {
  const canvasEyeRef = useRef<HTMLCanvasElement | null>(null);
  const canvasEagleRef = useRef<HTMLCanvasElement | null>(null);
  const eyePupilRef = useRef<HTMLDivElement | null>(null);

  const clipPathsForCircleQuarters = [
    // Bottom-right (0° to 90°)
    'polygon(50% 50%, 100% 50%, 100% 100%, 50% 100%)',

    // Bottom-left (90° to 180°)
    'polygon(50% 50%, 50% 100%, 0 100%, 0 50%)',

    // Top-left (180° to 270°)
    'polygon(50% 50%, 0 50%, 0 0, 50% 0)',

    // Top-right (270° to 360°)
    'polygon(50% 50%, 50% 0, 100% 0, 100% 50%)',
  ];

  const shuffleArray = (array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const EyeballAnimation = ({ delay, path }: { delay: number; path: string }) => {
    const duration = 0.25;

    return (
      <div
        className="absolute bg-blue-500 "
        style={{
          top: '50%',
          left: '50%',
          width: '42%',
          height: '42%',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          animation: `glowExpand ${duration}s ease-out forwards ${delay}s`,
          pointerEvents: 'none',
          zIndex: -3,
          clipPath: path,
          opacity: 0,
        }}>
        <div
          className=" bg-blue-100 w-full h-full p-[17px]"
          style={{
            borderRadius: '50%',
            pointerEvents: 'none',
            animation: `glowExpandPadding ${duration}s ease-in forwards ${delay}s`,
          }}>
          <div
            className=" bg-blue-300 w-full h-full"
            style={{
              borderRadius: '50%',
              pointerEvents: 'none',
            }}></div>
        </div>
      </div>
    );
  };

  const loadImage = (imgSrc: string, canvasRef: MutableRefObject<HTMLCanvasElement | null>) => {
    if (!canvasRef?.current) return;

    const canvas = canvasRef.current;

    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imgSrc;

    img.onload = () => {
      const scale = 0.5; // 50 % Größe
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx.drawImage(img, 0, 0, img.width * scale, img.height * scale);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Remove green pixels
        if (isMostlyGreen(r, g, b)) {
          data[i + 3] = 0; // Alpha to 0 → transparent
        }
      }

      ctx.putImageData(imageData, 0, 0);
      setEyeRdy(true);
    };
  };

  useLayoutEffect(() => {
    loadImage(eyeImg, canvasEyeRef);
    loadImage(EagleImg, canvasEagleRef);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pupil = eyePupilRef.current;
    if (pupil) {
      if (!expanded) {
        pupil.style.animation = '';
        pupil.style.boxShadow = `0 0 0px 5px rgb(0, 145, 252)`;
      } else {
        pupil.style.animation = 'eye-pupil 0.35s ease-out forwards 0.5s';
      }
    }
  }, [expanded]);

  return (
    <div className={`relative flex rounded-full w-full h-full`} ref={ref}>
      {/* pupil Image */}
      <canvas
        ref={canvasEyeRef}
        id="eagle-eye"
        style={{
          zIndex: 10,
          top: '50%',
          left: '50%',
          width: '100%',
          height: '100%',
          transform: 'translate(-50.5%, -49%)  scaleX(-1)',
          position: 'absolute',
        }}
      />

      {/* Eagle Image */}
      <canvas
        ref={canvasEagleRef}
        id="eagle-head"
        className={`absolute flex w-[530%] h-[530%]`}
        style={{
          transform: 'translate(-36.3%,-27.2%) scaleX(-1)',
          zIndex: 11,
        }}
      />

      <div
        className="absolute"
        style={{
          width: '100%',
          height: '100%',
          top: '0%',
          left: '0%',
          zIndex: -7,

          background: 'radial-gradient(circle,rgb(156 195 253) 29%, rgba(0, 0, 0, 1) 57%)',
        }}></div>

      {/* eye-pupil */}
      <div
        className="absolute eye-pupil"
        ref={eyePupilRef}
        style={{
          top: '50%',
          left: '50%',
          width: '7%',
          height: '7%',
          borderRadius: '40%',
          transform: 'translate(-50%, -50%) scale(0.2)',
          animation: 'eye-pupil 0.35s ease-out forwards 0.5s',
          backgroundColor: 'rgb(142, 254, 255)',
          pointerEvents: 'none',
          zIndex: 20,
          opacity: 0.4,
        }}></div>

      {/* eye-ring */}
      <div
        className="absolute "
        style={{
          top: '50%',
          left: '50%',
          width: '15%',
          height: '15%',
          borderRadius: '20%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#0062FFF1',
          animation: 'eye-ring 0.35s ease-out forwards 1s',
          opacity: 0.25,
          pointerEvents: 'none',
          zIndex: -1,
        }}></div>

      {/* eyeball background */}
      <div
        className="absolute bg-gray-400 "
        style={{
          top: '50%',
          left: '50%',
          width: '45%',
          height: '45%',
          borderRadius: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          zIndex: -6,
        }}></div>

      {/* eyeball animate  */}
      {shuffleArray(clipPathsForCircleQuarters).map((clipPath, index) => {
        const delay = 0.5 + index * 0.1;

        return <EyeballAnimation key={index} delay={delay} path={clipPath} />;
      })}
    </div>
  );
});

export default AnimatedEagleEye;
